import React, { useEffect, useContext, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styles from './BranchDetail.module.css';

import { apiGetBranch, apiUpdateBranch, apiGetAllClusters } from '../api/branches';
import { apiListNightlyRuns, apiTriggerNightly, apiDeployNightlyRun, apiDeployNightlyRunFreeTier, apiGetNightlyRun } from '../api/nightly';
import { getBranchReleases } from '../api/releases';
import { WebSocketContext } from '../api/socket';
import { setAlert } from '../helpers/alerts';
import { sortReleases } from '../helpers/releases';

import BranchIdentityStrip    from '../components/branches/BranchIdentityStrip';
import BranchLifecycleSpine   from '../components/branches/BranchLifecycleSpine';
import BranchingOpsSection    from '../components/branches/BranchingOpsSection';
import NightlySection         from '../components/branches/NightlySection';
import ReleasesSection        from '../components/branches/ReleasesSection';
import ReleaseScheduleSection from '../components/branches/ReleaseScheduleSection';
import AutoRolloutSection     from '../components/branches/AutoRolloutSection';
import BranchDrawer           from '../components/branches/BranchDrawer';
import ConfirmDialog          from '../components/branches/ConfirmDialog';
import DeployNightlyDrawer    from '../components/branches/DeployNightlyDrawer';
import TriggerReleaseDialog   from '../components/branches/TriggerReleaseDialog';

const ICO_MOON   = 'M12 3a9 9 0 1 0 9 9c0-.46-.04-.92-.1-1.36A5.39 5.39 0 0 1 12.5 3.1 9.06 9.06 0 0 0 12 3z';
const ICO_LAYERS = 'm12 2 10 6-10 6L2 8l10-6z M2 16l10 6 10-6 M2 12l10 6 10-6';

const NIGHTLY_ICON_STYLE = {
  background: 'var(--blue-50)',
  border: '1px solid var(--blue-200)',
  color: 'var(--blue-600)',
};

export default function BranchDetail({ user }) {
  const { version } = useParams();
  const [searchParams] = useSearchParams();
  const ws = useContext(WebSocketContext);

  const [branch,         setBranch]         = useState({});
  const [loaded,         setLoaded]         = useState(false);
  const [error,          setError]          = useState(null);
  const [nightlyRuns,    setNightlyRuns]    = useState([]);
  const [nightlyPage,    setNightlyPage]    = useState(0);
  const [nightlySearch,  setNightlySearch]  = useState('');
  const [nightlyTotal,   setNightlyTotal]   = useState(0);
  const [latestNightlyRun,   setLatestNightlyRun]   = useState(null);
  const [nightlyGrandTotal,  setNightlyGrandTotal]  = useState(0);
  const [branchReleases, setBranchReleases] = useState([]);
  const [nextReleaseVersion, setNextReleaseVersion] = useState(null);
  const [allClusters,    setAllClusters]    = useState([]);
  const [clustersLoading,setClustersLoading]= useState(false);
  const [clustersLoaded, setClustersLoaded] = useState(false);
  const clustersRequestRef = useRef(null);
  const deepLinkAppliedRef = useRef(false);

  // Drawer stack: each entry { kind: 'run'|'build'|'branchingOp'|'op'|'task', id, data, name? }
  const [drawerStack, setDrawerStack] = useState([]);
  const pushDrawer  = (entry) => setDrawerStack((s) => [...s, entry]);
  const popDrawer   = ()      => setDrawerStack((s) => s.slice(0, -1));
  const popToDrawer = (index) => setDrawerStack((s) => s.slice(0, index + 1));
  const closeDrawer = ()      => setDrawerStack([]);
  const nightlyRunById = new Map(nightlyRuns.map((run) => [run.id, run]));
  const resolvedDrawerStack = drawerStack.map((entry) => (
    entry.kind === 'run' && nightlyRunById.has(entry.id)
      ? { ...entry, data: nightlyRunById.get(entry.id) }
      : entry
  ));

  // Dialogs / confirmation
  const [nightlyConfirmOpen,    setNightlyConfirmOpen]    = useState(false);
  const [deployingRun,          setDeployingRun]          = useState(null);
  const [deployDrawerOpen,      setDeployDrawerOpen]      = useState(false);
  const [freeTierRun,           setFreeTierRun]           = useState(null);
  const [candidatesDrawerOpen,  setCandidatesDrawerOpen]  = useState(false);
  const [deployConfirmOpen,     setDeployConfirmOpen]     = useState(false);
  const [deployCandidateIds,    setDeployCandidateIds]    = useState([]);
  const [deploySavedCandidateIds, setDeploySavedCandidateIds] = useState([]);
  const [releaseDialogOpen,     setReleaseDialogOpen]     = useState(false);

  useEffect(() => {
    setNightlyPage(0);
    setNightlySearch('');
    reloadData();
    loadNightlyRuns(0, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    deepLinkAppliedRef.current = true;
    const runNumber = searchParams.get('run');
    const opId = searchParams.get('op');
    if (!runNumber && !opId) return;
    if (runNumber) {
      apiGetNightlyRun(`${version}n${runNumber}`)
        .then((r) => {
          pushDrawer({ kind: 'run', id: r.nightly_run.id, data: r.nightly_run });
          if (opId) pushDrawer({ kind: 'op', id: opId });
        })
        .catch(() => {});
    } else {
      pushDrawer({ kind: 'op', id: opId });
    }
    // Deep-link params are only meant to open the drawer once on initial load — deliberately
    // not re-run on searchParams/version change (no re-trigger if the user closes the drawer).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ws) return;
    ws.on('branch_update', onWSBranchUpdate);
    ws.on('nightly_update', onWSNightlyUpdate);
    return () => { ws.off('branch_update'); ws.off('nightly_update'); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws, version]);

  useEffect(() => {
    loadNightlyRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nightlyPage, nightlySearch]);

  const loadClusters = () => {
    if (clustersLoaded || clustersRequestRef.current) return;
    setClustersLoading(true);
    clustersRequestRef.current = apiGetAllClusters()
      .then((r) => {
        setAllClusters(r.clusters || []);
        setClustersLoaded(true);
      })
      .catch(() => {})
      .finally(() => {
        setClustersLoading(false);
        clustersRequestRef.current = null;
      });
  };

  const loadNightlyRuns = (pageOverride, searchOverride) => {
    const page = pageOverride ?? nightlyPage;
    const search = searchOverride ?? nightlySearch;
    apiListNightlyRuns(version, { limit: 10, offset: page * 10, search: search || undefined })
      .then((r) => {
        setNightlyRuns(r.nightly_runs || []);
        setNightlyTotal(r.total || 0);
      })
      .catch(() => {});
  };

  const loadLatestNightlyRun = () => {
    apiListNightlyRuns(version, { limit: 1, include_deployments: false })
      .then((r) => {
        setLatestNightlyRun((r.nightly_runs || [])[0] || null);
        setNightlyGrandTotal(r.total || 0);
      })
      .catch(() => {});
  };

  const reloadData = () => {
    apiGetBranch(version)
      .then((r) => {
        setBranch(r.branch);
        setNextReleaseVersion(r.next_release_version ?? null);
        setLoaded(true);
      })
      .catch((e) => { setError(e); setLoaded(true); });
    loadLatestNightlyRun();
    getBranchReleases(version)
      .then((r) => setBranchReleases((r.releases || []).sort(sortReleases)))
      .catch(() => {});
    loadClusters();
  };

  const onWSBranchUpdate = (update) => {
    if (update.branch?.version !== version) return;
    reloadData();
    loadNightlyRuns();
  };

  // Merge, not replace: some emitters send the bare run without deployments or rollout links
  const onWSNightlyUpdate = (update) => {
    const run = update.nightly_run;
    if (run?.branch_version !== version) return;
    setNightlyRuns((runs) => runs.map((r) => (r.id === run.id ? { ...r, ...run } : r)));
  };

  const handleTriggerNightly = () => {
    setNightlyConfirmOpen(false);
    apiTriggerNightly(version)
      .then((r) => {
        setAlert('Nightly', `Nightly run #${r.nightly_run.number} triggered`, 'success');
        reloadData();
        loadNightlyRuns();
      })
      .catch((e) => setAlert('Nightly error', e, 'danger'));
  };

  const handleOpenDeploy = (run) => {
    setDeployingRun(nightlyRunById.get(run.id) || run);
    setDeployDrawerOpen(true);
  };

  const handleDeployNightly = () => {
    setDeployConfirmOpen(false);
    setDeployDrawerOpen(false);
    apiUpdateBranch(version, { build_candidates: deploySavedCandidateIds })
      .then(() => apiDeployNightlyRun(deployingRun.id, deployCandidateIds))
      .then((r) => {
        const message = r.nightly_run.rollout_status === 'replicating'
          ? `Replication started for nightly #${r.nightly_run.number} — rollout will begin once the image is replicated`
          : `Deploy triggered for nightly #${r.nightly_run.number}`;
        setAlert('Deploy', message, 'success');
        reloadData();
        loadNightlyRuns();
      })
      .catch((e) => setAlert('Deploy error', e, 'danger'));
  };

  const handleDeployFreeTier = () => {
    const run = freeTierRun;
    setFreeTierRun(null);
    apiDeployNightlyRunFreeTier(run.id)
      .then((r) => {
        const rollout = r.nightly_run.free_tier_rollout || {};
        if (rollout.status === 'failed') {
          setAlert('Free tier error', rollout.error || 'The free-tier rollout could not be started', 'danger');
        } else {
          setAlert('Free tier', `Free-tier rollout started for nightly #${r.nightly_run.number}`, 'success');
        }
        loadNightlyRuns();
      })
      .catch((e) => setAlert('Free tier error', e, 'danger'));
  };

  const handleSaveBuildCandidates = (candidateIds) => {
    apiUpdateBranch(version, { build_candidates: candidateIds || [] })
      .then(() => {
        setCandidatesDrawerOpen(false);
        setAlert('Build candidates', `Build candidates updated for "${version}"`, 'success');
        reloadData();
      })
      .catch((e) => setAlert('Build candidates error', e, 'danger'));
  };

  if (!loaded) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner} />
        Loading branch…
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorPage}>
        <div className={styles.errorTitle}>Failed to load branch</div>
        <div className={styles.errorMsg}>{String(error)}</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <BranchIdentityStrip branch={branch} reloadData={reloadData} />

      <BranchLifecycleSpine
        branch={branch}
        nightlyTotal={nightlyGrandTotal}
        latestNightlyRun={latestNightlyRun}
        releases={branchReleases}
      />

      <div className={styles.inner}>
        <BranchingOpsSection
          branch={branch}
          onOpenOp={({ component, op }) => pushDrawer({
            kind: 'op',
            id: op.operation_id,
            name: component,
            data: { ...op, component, name: component },
          })}
        />

        <AutoRolloutSection
          branch={branch}
          onEditBuildCandidates={() => setCandidatesDrawerOpen(true)}
          reloadData={reloadData}
        />

        <NightlySection
          key={version}
          runs={nightlyRuns}
          page={nightlyPage}
          totalCount={nightlyTotal}
          onPageChange={setNightlyPage}
          onSearchChange={(q) => { setNightlySearch(q); setNightlyPage(0); }}
          onTrigger={() => setNightlyConfirmOpen(true)}
          onOpenRun={(run) => pushDrawer({ kind: 'run', id: run.id, data: run })}
        />

        <ReleaseScheduleSection
          branch={branch}
          nextVersion={nextReleaseVersion}
          reloadData={reloadData}
        />

        <ReleasesSection
          releases={branchReleases}
          onTrigger={() => setReleaseDialogOpen(true)}
        />
      </div>

      <BranchDrawer
        drawerStack={resolvedDrawerStack}
        onPush={pushDrawer}
        onPop={popDrawer}
        onPopTo={popToDrawer}
        onClose={closeDrawer}
        onOpenDeploy={handleOpenDeploy}
        onDeployFreeTier={setFreeTierRun}
        version={version}
        allClusters={allClusters}
      />

      <ConfirmDialog
        open={nightlyConfirmOpen}
        icon={ICO_MOON}
        iconStyle={NIGHTLY_ICON_STYLE}
        title="Trigger nightly run?"
        message={`Branch ${version} will be built and all tests will run automatically.`}
        confirmLabel="Trigger nightly"
        onConfirm={handleTriggerNightly}
        onCancel={() => setNightlyConfirmOpen(false)}
      />

      <DeployNightlyDrawer
        open={deployDrawerOpen}
        onClose={() => setDeployDrawerOpen(false)}
        onConfirm={(candidateIds, savedCandidateIds) => {
          setDeployCandidateIds(candidateIds || []);
          setDeploySavedCandidateIds(savedCandidateIds || candidateIds || []);
          setDeployConfirmOpen(true);
        }}
        run={deployingRun ? nightlyRunById.get(deployingRun.id) || deployingRun : null}
        latestRunNumber={latestNightlyRun?.number ?? null}
        branch={branch}
        user={user}
        allClusters={allClusters}
        clustersLoading={clustersLoading}
      />

      <DeployNightlyDrawer
        open={candidatesDrawerOpen}
        candidateOnly
        onClose={() => setCandidatesDrawerOpen(false)}
        onConfirm={handleSaveBuildCandidates}
        branch={branch}
        user={user}
        allClusters={allClusters}
        clustersLoading={clustersLoading}
      />

      <ConfirmDialog
        open={!!freeTierRun}
        icon={ICO_LAYERS}
        iconStyle={NIGHTLY_ICON_STYLE}
        title="Deploy to free tier?"
        message={`Every running free-tier cluster will be upgraded to ${freeTierRun?.build_tag}, and new free-tier clusters will be created on it.`}
        confirmLabel="Deploy to free tier"
        onConfirm={handleDeployFreeTier}
        onCancel={() => setFreeTierRun(null)}
      />

      <ConfirmDialog
        open={deployConfirmOpen}
        icon={ICO_LAYERS}
        iconStyle={NIGHTLY_ICON_STYLE}
        title="Deploy nightly build?"
        message={`The selected clusters will be upgraded to this nightly's build.`}
        confirmLabel="Deploy"
        onConfirm={handleDeployNightly}
        onCancel={() => setDeployConfirmOpen(false)}
      />

      {branch.version && (
        <TriggerReleaseDialog
          open={releaseDialogOpen}
          onClose={() => setReleaseDialogOpen(false)}
          branch={branch}
          onTriggered={reloadData}
        />
      )}
    </div>
  );
}
